import { type FC, useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { SearchInput, Drawer, useToast } from '@/components/base';
import { useLegalProducts } from '@/hooks/useLegalProducts';
import {
  type CreateFeeRuleInput,
  type FeeRule,
  useFeeRules,
} from '@/hooks/useFeeRules';

type FeeRuleFormState = {
  legalProductId: string;
  name: string;
  description: string;
  feeType: string;
  baseFee: string;
  entryFee: string;
  successFeePercentage: string;
  minFee: string;
  maxFee: string;
  installmentCount: string;
  installmentValue: string;
  dueDay: string;
  isDefault: boolean;
  paymentNotes: string;
  notes: string;
};

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const formatCurrency = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : currencyFormatter.format(value);

const formatPercentage = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${value}%`;

const toNullableText = (value: string) => {
  const text = value.trim();
  return text === '' ? null : text;
};

const toNullableNumber = (value: string) => {
  const text = value.trim();
  if (text === '') {
    return null;
  }

  const number = Number(text.replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const toNullableInteger = (value: string) => {
  const text = value.trim();
  if (text === '') {
    return null;
  }

  const number = Number(text.replace(',', '.'));
  return Number.isInteger(number) ? number : null;
};

const emptyFormState = (legalProductId = ''): FeeRuleFormState => ({
  legalProductId,
  name: '',
  description: '',
  feeType: 'fixed',
  baseFee: '',
  entryFee: '',
  successFeePercentage: '',
  minFee: '',
  maxFee: '',
  installmentCount: '',
  installmentValue: '',
  dueDay: '',
  isDefault: false,
  paymentNotes: '',
  notes: '',
});

const feeRuleToFormState = (feeRule: FeeRule, legalProductIdFallback = ''): FeeRuleFormState => ({
  legalProductId: feeRule.legalProductId || legalProductIdFallback,
  name: feeRule.name,
  description: feeRule.description ?? '',
  feeType: feeRule.feeType || 'fixed',
  baseFee: feeRule.baseFee === null || feeRule.baseFee === undefined ? '' : String(feeRule.baseFee),
  entryFee: feeRule.entryFee === null || feeRule.entryFee === undefined ? '' : String(feeRule.entryFee),
  successFeePercentage:
    feeRule.successFeePercentage === null || feeRule.successFeePercentage === undefined
      ? ''
      : String(feeRule.successFeePercentage),
  minFee: feeRule.minFee === null || feeRule.minFee === undefined ? '' : String(feeRule.minFee),
  maxFee: feeRule.maxFee === null || feeRule.maxFee === undefined ? '' : String(feeRule.maxFee),
  installmentCount:
    feeRule.installmentCount === null || feeRule.installmentCount === undefined
      ? ''
      : String(feeRule.installmentCount),
  installmentValue:
    feeRule.installmentValue === null || feeRule.installmentValue === undefined
      ? ''
      : String(feeRule.installmentValue),
  dueDay: feeRule.dueDay === null || feeRule.dueDay === undefined ? '' : String(feeRule.dueDay),
  isDefault: feeRule.isDefault,
  paymentNotes: feeRule.paymentNotes ?? '',
  notes: feeRule.notes ?? '',
});

const formatRuleLabel = (rule: FeeRule) => {
  const productName = rule.legalProductName ?? rule.name;
  const ruleName = rule.name && rule.name !== productName ? rule.name : '';

  return ruleName ? `${productName} • ${ruleName}` : productName;
};

const FeesPage: FC = () => {
  const { canManageLegalStructures } = useRolePermissions();
  const { addToast } = useToast();
  const {
    feeRules,
    isLoadingFeeRules,
    feeRulesError,
    loadFeeRules,
    createFeeRule,
    updateFeeRule,
    deleteFeeRule,
  } = useFeeRules({ autoLoad: false });
  const {
    legalProducts,
    isLoadingLegalProducts,
    legalProductsError,
    loadLegalProducts,
  } = useLegalProducts({ autoLoad: false });
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isSavingFeeRule, setIsSavingFeeRule] = useState(false);
  const [calcFeeRuleId, setCalcFeeRuleId] = useState('');
  const [calcComplexity, setCalcComplexity] = useState<'normal' | 'alta'>('normal');
  const [calcUrgency, setCalcUrgency] = useState(false);
  const [calcRisk, setCalcRisk] = useState<'baixo' | 'medio' | 'alto'>('baixo');
  const [form, setForm] = useState<FeeRuleFormState>(emptyFormState());
  const [editingFeeRule, setEditingFeeRule] = useState<FeeRule | null>(null);
  const [deletingFeeRuleId, setDeletingFeeRuleId] = useState('');

  useEffect(() => {
    void loadLegalProducts();
    void loadFeeRules();
  }, [loadFeeRules, loadLegalProducts]);

  const filteredFeeRules = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return feeRules;
    }

    return feeRules.filter((rule) =>
      [
        rule.legalProductName,
        rule.name,
        rule.description,
        rule.feeType,
        rule.paymentNotes,
        rule.notes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [feeRules, search]);

  const selectedFeeRule = useMemo(
    () => feeRules.find((rule) => rule.id === calcFeeRuleId) ?? null,
    [calcFeeRuleId, feeRules]
  );

  const selectedRuleSuggestion = useMemo(() => {
    if (!selectedFeeRule) {
      return null;
    }

    let base = selectedFeeRule.baseFee ?? selectedFeeRule.minFee ?? 0;
    let urgencyAdded = 0;
    let complexityAdded = 0;

    if (calcComplexity === 'alta') {
      complexityAdded = base * 0.1;
      base += complexityAdded;
    }

    if (calcUrgency) {
      urgencyAdded = base * 0.15;
      base += urgencyAdded;
    }

    if (calcRisk === 'alto') {
      base += base * 0.15;
    }

    const rounded = Math.round(base / 100) * 100;
    const entrySuggested =
      selectedFeeRule.entryFee !== null && selectedFeeRule.entryFee !== undefined
        ? Math.round(selectedFeeRule.entryFee / 100) * 100
        : rounded > 0
          ? Math.round((rounded * 0.4) / 100) * 100
          : 0;
    const installmentsSuggested =
      selectedFeeRule.installmentCount && selectedFeeRule.installmentCount > 1
        ? selectedFeeRule.installmentCount
        : 1;
    const installmentValue =
      selectedFeeRule.installmentValue !== null && selectedFeeRule.installmentValue !== undefined
        ? Math.round(selectedFeeRule.installmentValue / 100) * 100
        : installmentsSuggested > 1
          ? Math.round(((rounded - entrySuggested) / installmentsSuggested) / 100) * 100
          : 0;

    return {
      base: selectedFeeRule.baseFee ?? base,
      urgencyAdded,
      complexityAdded,
      total: rounded,
      entrySuggested,
      installmentsSuggested,
      installmentValue,
    };
  }, [calcComplexity, calcRisk, calcUrgency, selectedFeeRule]);

  useEffect(() => {
    if (feeRules.length === 0) {
      setCalcFeeRuleId('');
      return;
    }

    if (!feeRules.some((rule) => rule.id === calcFeeRuleId)) {
      setCalcFeeRuleId(feeRules[0].id);
    }
  }, [calcFeeRuleId, feeRules]);

  const openCreateForm = () => {
    if (!legalProducts.length) {
      addToast(
        'Cadastre um produto jurídico antes de criar uma regra de honorários.',
        'warning'
      );
      return;
    }

    setEditingFeeRule(null);
    setForm(emptyFormState(legalProducts[0]?.id ?? ''));
    setShowForm(true);
  };

  const openEditForm = (feeRule: FeeRule) => {
    setEditingFeeRule(feeRule);
    setForm(feeRuleToFormState(feeRule, legalProducts[0]?.id ?? ''));
    setShowForm(true);
  };

  const closeForm = () => {
    if (isSavingFeeRule) {
      return;
    }

    setShowForm(false);
    setEditingFeeRule(null);
    setForm(emptyFormState(legalProducts[0]?.id ?? ''));
  };

  const buildPayload = (): CreateFeeRuleInput => ({
    legalProductId: form.legalProductId.trim(),
    name: form.name.trim(),
    description: toNullableText(form.description),
    feeType: toNullableText(form.feeType) || 'fixed',
    baseFee: toNullableNumber(form.baseFee),
    entryFee: toNullableNumber(form.entryFee),
    successFeePercentage: toNullableNumber(form.successFeePercentage),
    minFee: toNullableNumber(form.minFee),
    maxFee: toNullableNumber(form.maxFee),
    installmentCount: toNullableInteger(form.installmentCount),
    installmentValue: toNullableNumber(form.installmentValue),
    dueDay: toNullableInteger(form.dueDay),
    isDefault: form.isDefault,
    paymentNotes: toNullableText(form.paymentNotes),
    notes: toNullableText(form.notes),
  });

  const handleSave = async () => {
    if (isSavingFeeRule) {
      return;
    }

    if (!form.legalProductId.trim()) {
      addToast('Selecione um produto jurídico.', 'warning');
      return;
    }

    if (!form.name.trim()) {
      addToast('Preencha o nome da regra de honorários.', 'warning');
      return;
    }

    setIsSavingFeeRule(true);

    try {
      const payload = buildPayload();

      if (editingFeeRule) {
        await updateFeeRule(editingFeeRule.id, payload);
        addToast('Regra de honorários atualizada com sucesso.', 'success');
      } else {
        await createFeeRule(payload);
        addToast('Regra de honorários criada com sucesso.', 'success');
      }

      setShowForm(false);
      setEditingFeeRule(null);
      setForm(emptyFormState(legalProducts[0]?.id ?? ''));
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível salvar a regra de honorários.', 'error');
    } finally {
      setIsSavingFeeRule(false);
    }
  };

  const handleDeleteFeeRule = async (feeRule: FeeRule) => {
    if (isSavingFeeRule || deletingFeeRuleId) {
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja excluir esta regra de honorários? Esta ação removerá a regra da listagem, mas manterá o registro no banco para auditoria.'
    );

    if (!confirmed) {
      return;
    }

    setDeletingFeeRuleId(feeRule.id);

    try {
      await deleteFeeRule(feeRule.id);
      addToast('Regra de honorários excluída com sucesso.', 'success');

      if (editingFeeRule?.id === feeRule.id) {
        setShowForm(false);
        setEditingFeeRule(null);
        setForm(emptyFormState(legalProducts[0]?.id ?? ''));
      }
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível excluir a regra de honorários.', 'error');
    } finally {
      setDeletingFeeRuleId('');
    }
  };

  const saveButtonClassName = `px-3 py-1.5 rounded-lg text-xs font-medium text-background-50 transition-colors cursor-pointer whitespace-nowrap ${
    isSavingFeeRule ? 'bg-primary-300' : 'bg-primary-500 hover:bg-primary-600'
  }`;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">

        {!canManageLegalStructures ? (
          <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
            Modo consulta: seu perfil pode visualizar este módulo, mas alterações estruturais são reservadas a administradores e advogados.
          </div>
        ) : null}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Honorários</h2>
            <p className="text-sm text-foreground-500 mt-0.5">
              Tabela de honorários por produto jurídico
            </p>
          </div>
          <button
            hidden={!canManageLegalStructures}
            onClick={openCreateForm}
            disabled={isLoadingLegalProducts || legalProducts.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <i className="ri-add-line"></i> Novo Honorário
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar regra ou produto..."
            className="sm:w-72"
          />
        </div>

        {isLoadingFeeRules && (
          <div className="rounded-lg border border-dashed border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-500">
            Carregando regras de honorários...
          </div>
        )}

        {feeRulesError && !isLoadingFeeRules && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-3">
            <span>{feeRulesError}</span>
            <button
              onClick={() => void loadFeeRules()}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {legalProductsError && !isLoadingLegalProducts && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-3">
            <span>{legalProductsError}</span>
            <button
              onClick={() => void loadLegalProducts()}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!isLoadingLegalProducts && !legalProductsError && legalProducts.length === 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Cadastre um produto jurídico antes de criar uma regra de honorários.
          </div>
        )}

        <div className="bg-background-50 border border-accent-200/50 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
            <i className="ri-calculator-line text-accent-500"></i>
            Sugestão de Honorários
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Regra</label>
              <select
                value={calcFeeRuleId}
                onChange={(e) => setCalcFeeRuleId(e.target.value)}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                <option value="">Selecionar...</option>
                {feeRules.map((rule) => (
                  <option key={rule.id} value={rule.id}>
                    {formatRuleLabel(rule)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Complexidade</label>
              <select
                value={calcComplexity}
                onChange={(e) => setCalcComplexity(e.target.value as 'normal' | 'alta')}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Urgência</label>
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={() => setCalcUrgency(!calcUrgency)}
                  className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${
                    calcUrgency ? 'bg-red-500' : 'bg-background-200'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${
                      calcUrgency ? 'translate-x-5' : ''
                    }`}
                  ></span>
                </button>
                <span className="text-sm text-foreground-600">{calcUrgency ? 'Sim' : 'Não'}</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Risco</label>
              <select
                value={calcRisk}
                onChange={(e) => setCalcRisk(e.target.value as 'baixo' | 'medio' | 'alto')}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                <option value="baixo">Baixo</option>
                <option value="medio">Médio</option>
                <option value="alto">Alto</option>
              </select>
            </div>
          </div>

          {selectedRuleSuggestion !== null && (
            <div className="p-4 bg-accent-50/30 border border-accent-200/50 rounded-lg space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="p-2 bg-background-50 rounded-lg text-center">
                  <p className="text-[10px] text-foreground-400">Valor Base</p>
                  <p className="text-sm font-bold text-foreground-900">
                    {formatCurrency(selectedRuleSuggestion.base)}
                  </p>
                </div>
                {selectedRuleSuggestion.urgencyAdded > 0 && (
                  <div className="p-2 bg-red-50 rounded-lg text-center">
                    <p className="text-[10px] text-foreground-400">Ad. Urgência</p>
                    <p className="text-sm font-bold text-red-600">
                      +{formatCurrency(selectedRuleSuggestion.urgencyAdded)}
                    </p>
                  </div>
                )}
                {selectedRuleSuggestion.complexityAdded > 0 && (
                  <div className="p-2 bg-amber-50 rounded-lg text-center">
                    <p className="text-[10px] text-foreground-400">Ad. Complexidade</p>
                    <p className="text-sm font-bold text-amber-600">
                      +{formatCurrency(selectedRuleSuggestion.complexityAdded)}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">
                    Valor Final Sugerido
                  </p>
                  <p className="text-2xl font-bold text-foreground-900">
                    {formatCurrency(selectedRuleSuggestion.total)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-foreground-500">
                    Entrada sugerida:{' '}
                    <strong>{formatCurrency(selectedRuleSuggestion.entrySuggested)}</strong>
                  </p>
                  {selectedRuleSuggestion.installmentsSuggested > 1 && (
                    <p className="text-xs text-foreground-500">
                      {selectedRuleSuggestion.installmentsSuggested}x de{' '}
                      <strong>{formatCurrency(selectedRuleSuggestion.installmentValue)}</strong>
                    </p>
                  )}
                </div>
              </div>
              {selectedFeeRule && (
                <p className="text-xs text-foreground-500">
                  Base: {formatCurrency(selectedFeeRule.baseFee)} • Tipo: {selectedFeeRule.feeType}
                </p>
              )}
            </div>
          )}

          <div className="mt-3 p-2.5 bg-amber-50/50 border border-amber-100 rounded-lg">
            <p className="text-xs text-amber-700 font-medium">
              <i className="ri-information-line"></i> Sugestão interna. Decisão final do advogado.
            </p>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Produto / Regra
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Mínimo
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Base
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Máximo
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Entrada Mín.
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Parcelamento
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    % Êxito
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Tipo
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Padrão
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFeeRules.map((feeRule) => (
                  <tr
                    key={feeRule.id}
                    className="border-b border-background-100 hover:bg-background-50 transition-colors group"
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                        {feeRule.legalProductName ?? '—'}
                      </p>
                      <p className="text-[11px] text-foreground-500 whitespace-nowrap">
                        {feeRule.name}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm text-foreground-600 whitespace-nowrap">
                        {formatCurrency(feeRule.minFee)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-semibold text-primary-700 whitespace-nowrap">
                        {formatCurrency(feeRule.baseFee)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm text-foreground-600 whitespace-nowrap">
                        {formatCurrency(feeRule.maxFee)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm text-foreground-600 whitespace-nowrap">
                        {formatCurrency(feeRule.entryFee)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm text-foreground-600 whitespace-nowrap">
                        {feeRule.installmentCount && feeRule.installmentCount > 0
                          ? `Até ${feeRule.installmentCount}x`
                          : '—'}
                      </span>
                      {feeRule.installmentValue !== null && feeRule.installmentValue !== undefined && (
                        <p className="text-[10px] text-foreground-400 whitespace-nowrap">
                          {formatCurrency(feeRule.installmentValue)}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm text-foreground-600 whitespace-nowrap">
                        {formatPercentage(feeRule.successFeePercentage)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-medium text-foreground-600 whitespace-nowrap">
                        {feeRule.feeType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`text-sm font-medium whitespace-nowrap ${
                          feeRule.isDefault ? 'text-primary-700' : 'text-foreground-400'
                        }`}
                      >
                        {feeRule.isDefault ? 'Sim' : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
            hidden={!canManageLegalStructures}
                          onClick={() => openEditForm(feeRule)}
                          disabled={isSavingFeeRule || Boolean(deletingFeeRuleId)}
                          className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          Editar
                        </button>
                        <button
            hidden={!canManageLegalStructures}
                          onClick={() => void handleDeleteFeeRule(feeRule)}
                          disabled={isSavingFeeRule || deletingFeeRuleId === feeRule.id}
                          className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {deletingFeeRuleId === feeRule.id ? 'Excluindo...' : 'Excluir'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {!isLoadingFeeRules && !feeRulesError && filteredFeeRules.length === 0 && (
          <div className="rounded-xl border border-dashed border-background-200 bg-background-50 p-8 text-center">
            <p className="text-sm font-medium text-foreground-700">Nenhuma regra de honorários encontrada</p>
            <p className="text-xs text-foreground-500 mt-1">
              Ajuste a busca ou cadastre uma nova regra.
            </p>
          </div>
        )}

        <Drawer
          isOpen={canManageLegalStructures && showForm}
          onClose={closeForm}
          title={editingFeeRule ? 'Editar Regra de Honorários' : 'Nova Regra de Honorários'}
          width="w-[640px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeForm}
                disabled={isSavingFeeRule}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
            hidden={!canManageLegalStructures}
                onClick={handleSave}
                disabled={isSavingFeeRule || (!editingFeeRule && legalProducts.length === 0)}
                className={saveButtonClassName}
              >
                {isSavingFeeRule
                  ? 'Salvando...'
                  : editingFeeRule
                    ? 'Salvar Alterações'
                    : 'Salvar Honorário'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Produto Jurídico *
                </label>
                <select
                  value={form.legalProductId}
                  onChange={(e) => setForm((prev) => ({ ...prev, legalProductId: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                >
                  <option value="">Selecionar...</option>
                  {legalProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Nome da Regra *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Ex: Padrão Judicial"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Tipo de Cobrança</label>
                <select
                  value={form.feeType}
                  onChange={(e) => setForm((prev) => ({ ...prev, feeType: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                >
                  <option value="fixed">Fixo</option>
                  <option value="entry">Entrada</option>
                  <option value="success">Êxito</option>
                  <option value="hybrid">Híbrido</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Dia de Vencimento</label>
                <input
                  type="number"
                  step="1"
                  value={form.dueDay}
                  onChange={(e) => setForm((prev) => ({ ...prev, dueDay: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="10"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 w-full">
                  <input
                    type="checkbox"
                    checked={form.isDefault}
                    onChange={(e) => setForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
                    className="h-4 w-4 rounded border-background-300 text-primary-600 focus:ring-primary-500"
                  />
                  Regra padrão
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Base (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.baseFee}
                  onChange={(e) => setForm((prev) => ({ ...prev, baseFee: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="8500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Entrada (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.entryFee}
                  onChange={(e) => setForm((prev) => ({ ...prev, entryFee: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="3000"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">% Êxito</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.successFeePercentage}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, successFeePercentage: e.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="20"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Mínimo (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.minFee}
                  onChange={(e) => setForm((prev) => ({ ...prev, minFee: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="5000"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Máximo (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.maxFee}
                  onChange={(e) => setForm((prev) => ({ ...prev, maxFee: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="12000"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Parcelas</label>
                <input
                  type="number"
                  step="1"
                  value={form.installmentCount}
                  onChange={(e) => setForm((prev) => ({ ...prev, installmentCount: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="3"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Valor da Parcela (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.installmentValue}
                  onChange={(e) => setForm((prev) => ({ ...prev, installmentValue: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="2500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Descrição</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
                rows={3}
                placeholder="Descrição resumida da regra de honorários"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Observações de Pagamento
              </label>
              <textarea
                value={form.paymentNotes}
                onChange={(e) => setForm((prev) => ({ ...prev, paymentNotes: e.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
                rows={3}
                placeholder="Condições de pagamento, vencimento ou desconto"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Observações Internas</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
                rows={3}
                placeholder="Anotações internas sobre a regra"
              />
            </div>
          </div>
        </Drawer>
      </div>
    </AppLayout>
  );
};

export default FeesPage;
